import { Enums_DocumentType } from 'data/endpoints';
import { useQuery } from 'data/hooks/useQuery';
import PageCard from 'features/shared/components/card/PageCard';
import DocumentTypeEnumDTO from 'features/shared/enums/dynamic/DocumentTypeEnumDTO';
import FileInput from 'features/shared/forms/components/FileInput';
import FormGroup from 'features/shared/forms/components/FormGroup';
import { ValidationActionType } from 'features/shared/forms/hooks/useForm';
import { getQueryParams } from 'features/shared/helpers/queryParams';
import { Nullable } from 'features/shared/types/nullable';
import ValidationRule from 'features/validation/rules/ValidationRule';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { setDocumentsTypeValidation, setFileValidation } from './basicInfoValidation';
import RenderWrapper from 'features/shared/components/renderWrapper/RenderWrapper';

interface Props {
  bindField: any;
  bindUncontrolledField: any;
  errors: Record<string, string>;
  setValidationRule: (key: string, validationRule: Nullable<ValidationRule>, action?: ValidationActionType) => void;
  enableDescription?: boolean;
  cardTitle?: string;
}

interface RouteProps {
  type: string;
}

const BasicInfoSubFormSection = ({ bindField, bindUncontrolledField, errors, setValidationRule, enableDescription = false, cardTitle }: Props) => {
  // translations
  const { t } = useTranslation(["shared\\validations"]); 
  const tLabels = useTranslation("requirements\\requirements").t;

  // route paramas
  const { type } = useParams<RouteProps>();

  // fetch data
  const { data: documentTypes } = useQuery<DocumentTypeEnumDTO[]>({ url: Enums_DocumentType, params: getQueryParams("RequirementType", type) }, []);

  // refs
  const fileInputEl = useRef<HTMLInputElement>(null);

  // side effects
  useEffect(() => {
    setFileValidation(setValidationRule, t);
    setDocumentsTypeValidation(setValidationRule, fileInputEl);
  }, []);

  // helpers
  const getPageTitle = () => { 
    if (cardTitle) {
      return cardTitle;
    } else if (enableDescription) {
      return tLabels("basicInfoSubFormSection.cardTitle.descriptionAndDocuments");
    } else {
      return tLabels("basicInfoSubFormSection.cardTitle.documents");
    }
  }

  // render
  return (
    <PageCard title={getPageTitle()} bodyClassName="pt-4 px-4">
      <RenderWrapper isVisible={() => enableDescription}>
        <FormGroup labelText={tLabels("basicInfoSubFormSection.description")} inputId="description" validationError={errors["description"]}>
          <textarea id="description" className="form-control" rows={5} maxLength={2000} placeholder={tLabels("basicInfoSubFormSection.addRequestDescription")} {...bindField("description")} />
        </FormGroup>
      </RenderWrapper>
      <div className="row no-gutters mx-n2">
        <div className="col-lg-6 px-2">
          <FormGroup labelText={tLabels("basicInfoSubFormSection.documentsSelection")} inputId="files" validationError={errors["files"]}>
            <FileInput ref={fileInputEl} id="files" multiple {...bindUncontrolledField("files")} defaultText={tLabels("basicInfoSubFormSection.selectDocuments")} />
          </FormGroup>
        </div>
        <div className="col-lg-6 px-2">
          <FormGroup labelText={tLabels("basicInfoSubFormSection.documentsType")} inputId="documentsType" validationError={errors["documentsType"]}>
            <select id="documentsType" className="custom-select" {...bindField('documentsType')}>
              <option value="">{tLabels("basicInfoSubFormSection.selectTypeOfAddedDocuments")}</option>
              {documentTypes.map((documentType, index) => (
                <option value={documentType.documentTypeCode} key={index}>
                  {documentType.documentTypeName}
                </option>
              ))}
            </select>
          </FormGroup>
        </div>
      </div>
    </PageCard>
  )
}

export default BasicInfoSubFormSection;